/* eslint-disable max-len */
import * as am5 from '@amcharts/amcharts5';
import { AxisBullet, AxisRenderer, AxisRendererX, AxisRendererY, ColumnSeries, DateAxis, IAxisRendererXSettings, ISmoothedXLineSeriesSettings, IXYSeriesDataItem, LineSeries, SmoothedXLineSeries, ValueAxis, XYChart, XYCursor, XYSeries } from '@amcharts/amcharts5/xy';
import { TimeUnit } from '@amcharts/amcharts5/.internal/core/util/Time';
import moment from 'moment-timezone';
import * as am5xy from "@amcharts/amcharts5/xy";
import { maxBy, minBy } from 'lodash';

import { DateQueryTypeEnum } from '../../../../api/mentions/enum/date-query-type.enum';

import { Am5Chart } from '../am5-chart.model';
import { Am5DateValueXYChartConfiguration } from './am5-date-value-x-y-chart-configuration.model';
import { Am5DateValueXYChartSettings } from './am5-date-value-x-y-chart-settings.model';
import { ChartBulletTypesEnum } from '../../../../Common/Reports/enums/chart-bullet-types.enum';
import { chartColors } from '../../enums/chart-colors.enum';
import { ChartMetricsEnum, chartMetricsToDateAndValueXYChartUnitMap } from '../../../../Common/Reports/enums/chart-metrics.enum';
import { ChartSeries } from '../chart-series.model';
import { ChartSpecificTypesEnum } from '../../../../Common/Reports/enums/chart-specific-types.enum';
import { ChartTimeIntervalsEnum } from '../../../../Common/Reports/enums/chart-time-intervals.enum';
import { chartSpecificTypesToBaseAxisDirectionsMap, chartSpecificTypesToValueAxisDirectionsMap } from '../../../../Common/Reports/enums/report-block-type.enum';
import { currencySymbolMap } from '../../../../Core/Users/enums/user-currency';
import { DateAndValueXYChartUnitEnum } from '../../../../Common/Charts/enums/date-and-value-x-y-chart-unit.enum';
import { DataPointModel } from '../data-point.model';
import { DateQueryModel } from '../../../../Common/Forms/models/date-query.model';
import { defaultSeriesColorConfig } from '../../../../Common/Charts/config/default-series-color.config';
import { MentionSearchCriteriaModel } from '../../../../Monitor/Shared/models/mention-search-criteria.model';
import { StackedChartTypesEnum } from '../../../../Common/Reports/enums/stacked-chart-types.enum';
import { TimeHelper } from '../../../../Common/Forms/helpers/time.helper';
import { ValueAxisModel } from '../axis.model';


export class Am5DateValueXYChart extends Am5Chart {

    declare amchart: XYChart;
    declare chartType: typeof XYChart;
    declare config: Am5DateValueXYChartConfiguration;
    declare settings: Am5DateValueXYChartSettings;

    dateAxis: DateAxis<AxisRenderer>;
    valueAxis: ValueAxis<AxisRenderer>;
    valueAxes: ValueAxis<AxisRenderer>[] = [];
    bullets: am5.Bullet[] = [];
    timeHelper = new TimeHelper();
    private activeTooltipSprite: any = null;

    readonly maxCountForGrouping: number = 36;
    // data points before they are grouped to fit the chart, exported for a dynamic time interval
    public ungroupedExportData?: DataPointModel[];

    constructor(
        series: ChartSeries[],
        config: Am5DateValueXYChartConfiguration,
        settings: Am5DateValueXYChartSettings
    ) {
        super(series, config, settings);
    }

    public initChart(element: HTMLElement, chartType = XYChart) {
        super.initChart(element, chartType);
        this.isSingleSeries = this.series.length === 1;
        // set this to only show one tooltip
        this.amchart.set('maxTooltipDistance', -1);

        this.setUpFormatters();
        this.setUpDateAxis();
        this.setUpValueAxes();
        this.setUpCursor();
        this.setUpSeries(this.series);
        this.setUpLegend();
        this.setUpScrollbar();
        this.handleDateAxisRange();
        this.setUpExporting();
        this.resetZoomOut();
    }

    setUpFormatters(): void {
        this.amchart.root.numberFormatter.setAll({
            bigNumberPrefixes: [
                { 'number': 1e+3, 'suffix': 'K' },
                { 'number': 1e+6, 'suffix': 'M' },
                { 'number': 1e+9, 'suffix': 'B' },
                { 'number': 1e+12, 'suffix': 'T' },
                { 'number': 1e+15, 'suffix': 'Q' }
            ],
            smallNumberPrefixes: []
        });
    }

    setUpDateAxis(): void {
        const axis = this.amchart[chartSpecificTypesToBaseAxisDirectionsMap.get(this.settings.chartSpecificType)];
        const dateAxisRendererSettings: IAxisRendererXSettings = {
            minGridDistance: this.config.isStacked ? 60 : 50,
            cellStartLocation: 0.1,
            cellEndLocation: 0.9
        };

        const dateAxisRenderer = AxisRendererX.new(this.root, dateAxisRendererSettings);
        dateAxisRenderer.labels.template.setAll({
            fontFamily: 'Poppins, Arial, sans-serif',
            fontSize: 13,
            dy: 5
        });
        dateAxisRenderer.ticks.template.setAll({
            visible: true,
            stroke: am5.color(this.config.isStacked ? '#ced4da' : chartColors.agprText),
            length: this.config.isStacked ? 6 : 8,
            strokeWidth: 1,
            strokeOpacity: 1,
            location: 0
        });
        dateAxisRenderer.grid.template.set('location', 0);
        const dateAxisSettings: any = {
            renderer: dateAxisRenderer,
            gridIntervals: [
                { timeUnit: 'minute', count: 5 },
                { timeUnit: 'minute', count: 15 },
                { timeUnit: 'minute', count: 30 },
                { timeUnit: 'hour', count: 1 },
                { timeUnit: 'hour', count: 2 },
                { timeUnit: 'hour', count: 4 },
                { timeUnit: 'hour', count: 6 },
                { timeUnit: 'hour', count: 8 },
                { timeUnit: 'hour', count: 12 },
                { timeUnit: 'day', count: 1 },
                { timeUnit: 'day', count: 2 },
                { timeUnit: 'day', count: 7 },
                { timeUnit: 'month', count: 1 },
                { timeUnit: 'month', count: 3 },
                { timeUnit: 'year', count: 1 }
            ],
            groupIntervals: this.settings.groupInterval ?
                [this.settings.groupInterval]
                : [
                    { timeUnit: 'hour', count: 1 },
                    { timeUnit: 'day', count: 1 },
                    { timeUnit: 'week', count: 1 },
                    { timeUnit: 'month', count: 1 },
                    { timeUnit: 'month', count: 3 },
                    { timeUnit: 'year', count: 1 }
                ],
            groupData: this.settings.groupData,
            groupCount: this.maxCountForGrouping + 2,
            startLocation: this.config.isStacked ? 0.5 : 0,
            endLocation: this.config.isStacked ? 0.6 : 1,
        };
        if (this.settings.dateGranularity) {
            dateAxisSettings.baseInterval = this.settings.dateGranularity;
        }
        if (this.settings.groupInterval) {
            dateAxisSettings.groupInterval = this.settings.groupInterval;
        }
        if (!this.config.isStacked) {
            dateAxisSettings.extraMax = 0.03;
        }
        this.dateAxis = axis.push(DateAxis.new(this.root, dateAxisSettings));
        this.dateAxis.labelsContainer.set('paddingTop', 5);
    }

    setUpValueAxes(): void {
        this.series.forEach((series, index) => {
            if (series.requiresAxis) {
                const valueAxisModel: ValueAxisModel = {
                    key: `series_${index}`,
                    label: series.axisTitleText,
                    // valueLabel: this.options.yValueLabel,
                    unit: chartMetricsToDateAndValueXYChartUnitMap.get((series.key) as ChartMetricsEnum),
                    // percentLabel: this.options.percentLabel,
                    color: this.isSingleSeries ? null : this.settings.colorMap[`analysisColor${index + 1}`],
                    position: index === 0  ? 'left' : 'right',
                    hideGrid: series.hideAxisGrid,
                    percentageValueAxis: series.percentageValueAxis
                };
                const axis = this.addValueAxis(valueAxisModel, this.valueAxes[0]);
                this.valueAxes.push(axis);
            }
        });
        if (this.isSingleSeries && this.config.maxYValue) {
            this.valueAxes[0].set('max', this.config.maxYValue);
        }
    }

    addValueAxis(valueAxisModel: ValueAxisModel, syncAxis?: ValueAxis<AxisRenderer>): ValueAxis<AxisRenderer> {
        const axis = this.amchart[chartSpecificTypesToValueAxisDirectionsMap.get(this.settings.chartSpecificType)];
        const valueAxisRendererSettings: IAxisRendererXSettings = {
            inversed: this.config?.forceInverseMinMax ? true : false
        };
        if (valueAxisModel.position === 'right') {
            this.amchart.rightAxesContainer.set('reverseChildren', true);
            valueAxisRendererSettings.opposite = true;
        }
        if (valueAxisModel.color) {
            valueAxisRendererSettings.stroke = am5.color(valueAxisModel.color);
            valueAxisRendererSettings.strokeWidth = 2;
            valueAxisRendererSettings.strokeOpacity = 1;
        }
        const valueAxisRenderer = AxisRendererY.new(this.root, valueAxisRendererSettings);
        valueAxisRenderer.labels.template.set('fontFamily', 'Poppins, Arial, sans-serif');
        valueAxisRenderer.labels.template.set('fontSize', 14);
        if (valueAxisModel.position === 'right') {
            valueAxisRenderer.labels.template.set('centerX', am5.p0);
        }
        if (valueAxisModel.percentageValueAxis) {
            valueAxisRenderer.labels.template.adapters.add('text', function (text) {
                if (text) {
                    if (!text.endsWith('%')) {
                        return text + '%';
                    }
                    return text;
                }
                return '';
            });
        }
        const valueAxisSettings: any = {
            renderer: valueAxisRenderer,
            min: 0,
            maxPrecision: 0,
        };
        if (this.config.isStacked) {
            if (this.settings.stackedChartType === StackedChartTypesEnum.PERCENTAGE) {
                valueAxisSettings.max = 100;
                valueAxisSettings.strictMinMax = true;
                valueAxisSettings.calculateTotals = true;
            } else {
                valueAxisSettings.extraMax = 0.1;
            }
        }

        if (this.config?.forceInverseMinMax) {
            valueAxisSettings.strictMinMax = true;
            valueAxisSettings.maxPrecision = 0;
            valueAxisSettings.extraMax = 0;
            valueAxisSettings.extraMin = 0;
        }

        const valueAxis = ValueAxis.new(this.root, valueAxisSettings);
        if (valueAxisModel.label) {
            const isRightAxis = valueAxisModel.position === 'right';
            const valueAxisLabel = am5.Label.new(this.root, {
                rotation: isRightAxis ? 90 : -90,
                text: valueAxisModel.label,
                y: am5.p50,
                centerX: am5.p50
            });
            if (isRightAxis) {
                valueAxis.children.push(valueAxisLabel);
            } else {
                valueAxis.children.unshift(valueAxisLabel);
            }
        }
        if (valueAxisModel.unit === DateAndValueXYChartUnitEnum.CURRENCY) {
            const currency = this.settings.currencyCode;
            if (currency) {
                const currencySymbol = currencySymbolMap.get(currency);
                if (currencySymbol) {
                    valueAxis.set('numberFormat', `${currencySymbol}#,###.#a`);
                } else {
                    valueAxis.set('numberFormat', `#,###.#a ${currency}`);
                }
            } else {
                valueAxis.set('numberFormat', '#,###.#a');
            }
        } else {
            valueAxis.set('numberFormat', '#.#a');
        }

        if (syncAxis) {
            if (this.amchart.yAxes.indexOf(valueAxis) > 0) {
                valueAxis.set('syncWithAxis', this.amchart.yAxes.getIndex(0) as ValueAxis<AxisRenderer>);
            }
        }

        axis.push(valueAxis);
        if (syncAxis) {
            if (this.amchart.yAxes.indexOf(valueAxis) > 0) {
                valueAxis.set('syncWithAxis', this.amchart.yAxes.getIndex(0) as ValueAxis<AxisRenderer>);
            }
        }

        return valueAxis;
    }

    setUpCursor(){
        if (this.config.useChartCursor) {
            this.amchart.set('cursor', XYCursor.new(this.root, {}));
            const cursor = this.amchart.get('cursor');
            cursor.lineX.setAll({
                stroke: am5.color(chartColors.agprWhite),
                strokeWidth: 1,
                strokeOpacity: 1
            });
            cursor.lineY.setAll({
                visible: false
            });
        }
    }

    setUpSeries(chartSeries: ChartSeries[]): void {
        if (this.destroyed) {
            return;
        }
        chartSeries.forEach((series, index) => {
            this.addSeries(index, series);
        });
    }

    addSeries(
        seriesIndex: number,
        chartSeries: ChartSeries,
    ): void {
        const metricType = chartSeries.key as ChartMetricsEnum;
        const color = this.settings.colorMap[chartSeries.colorMapKey]
            || defaultSeriesColorConfig[seriesIndex % defaultSeriesColorConfig.length];

        let series: LineSeries | SmoothedXLineSeries | ColumnSeries;
        switch (chartSeries.chartSpecificType) {
            case ChartSpecificTypesEnum.LINE:
            case ChartSpecificTypesEnum.MULTI_LINE:
            case ChartSpecificTypesEnum.FILLED_LINE:
                series = this.setUpLineSeries(color, seriesIndex, chartSeries);
                break;
            case ChartSpecificTypesEnum.COLUMN:
                series = this.setUpColumnSeries(color, seriesIndex, metricType, chartSeries);
                break;
        }

        if (series) {
            series['name'] = chartSeries.label;
        }
    }

    setUpLineSeries(
        color: string,
        chartSeriesIndex: number,
        chartSeries: ChartSeries
    ): LineSeries {
        this.dateAxis.get('renderer').grid.template.set('visible', false);

        const isPercentageStack = this.config.isStacked
            && this.settings.stackedChartType === StackedChartTypesEnum.PERCENTAGE;

        const lineSeriesSettings: ISmoothedXLineSeriesSettings = {
            xAxis: this.dateAxis,
            yAxis: this.valueAxes[chartSeries.axisIndex],
            valueXField: 'key',
            valueYField: 'value' + chartSeriesIndex,
            valueYGrouped: 'sum',
            tension: 0.4,
            stroke: am5.color(color),
            fill: am5.color(color),
            locationX: 0.5,
            maskBullets: false,
            // tooltip: tooltip,
            groupDataCallback: this.groupDataItem.bind(this)
        };
        if (this.config.isStacked) {
            lineSeriesSettings.stacked = true;
            if (isPercentageStack) {
                lineSeriesSettings.valueYShow = 'valueYTotalPercent';
            }
        }
        const lineSeries = SmoothedXLineSeries.new(this.root, lineSeriesSettings);

        lineSeries.strokes.template.setAll({
            stroke: am5.color(color),
            strokeWidth: 2
        });
        if (chartSeries.strokeOpacity) {
            lineSeries.strokes.template.set('strokeOpacity', chartSeries.strokeOpacity);
        }
        if (chartSeries.strokeDasharray) {
            lineSeries.strokes.template.set('strokeDasharray', chartSeries.strokeDasharray as number[]);
        }

        if (this.settings.chartSpecificType === ChartSpecificTypesEnum.FILLED_LINE) {
            lineSeries.fills.template.set('fillGradient', am5.LinearGradient.new(this.root, {
                stops: [
                    { opacity: 1 },
                    { opacity: 0 }
                ],
            }));

            lineSeries.fills.template.set('visible', true);
        }

        if (this.config.isStacked) {
            lineSeries.fills.template.setAll({
                visible: true,
                fillOpacity: 0.9
            });
            this.setUpStackedSeriesTooltip(lineSeries, chartSeries, chartSeriesIndex);
        } else {
            this.setUpBullets(lineSeries, color, chartSeries, chartSeriesIndex);
        }

        return this.amchart.series.push(lineSeries);
    }

    setUpBullets(
        series: SmoothedXLineSeries,
        color: string,
        chartSeries: ChartSeries,
        chartSeriesIndex: number
    ): void {
        series.bullets.push((root, s, dataItem) => {
            const dataItems = s.dataItems;
            const lastDataItem = dataItems[dataItems.length - 1];
            if (chartSeries.showLastItem && dataItem !== lastDataItem) {
                return undefined;
            }

            const tooltip = am5.Tooltip.new(this.root, {
                getFillFromSprite: false,
                labelHTML: chartSeries.tooltipHTML,
                hoverOnFocus: true,
                keepTargetHover: true,
                stateAnimationDuration: 0
            });
            (tooltip.get('background') as am5.PointedRectangle).setAll({
                fill: am5.color(chartColors.agprWhite),
                fillOpacity: 1,
                stroke: am5.color('#ddd'),
                cornerRadius: 8
            });
            tooltip.events.on('click', (event) => {
                this.tooltipLinkClicked$.next((event.target as am5.Tooltip).label.dataItem.dataContext?.['viewUrl' + chartSeriesIndex]);
            });

            let sprite: any = am5.Circle.new(this.root, {
                radius: 3.5,
                fill: am5.color(chartColors.agprWhite),
                stroke: am5.color(color),
                strokeWidth: 1,
                focusable: true,
                hoverOnFocus: true,
                interactive: true,
                showTooltipOn: 'hover',
                tooltipText: this.config.useTooltip ? (chartSeries.tooltipText  || ' ') : null, // official HACK to make sure the tooltip displays
                tooltip: tooltip
            });
            if (this.config.useTooltip) {
                sprite.states.create('hover', {
                    radius: 4.5
                });
            }

            let pointerOverEvent, pointerMoveEvent, directionCheck = true;
            sprite.events.on('pointerover', (event) => {
                pointerOverEvent = event;
                if (this.activeTooltipSprite && this.activeTooltipSprite !== sprite) {
                    this.activeTooltipSprite.forceHideTooltip();
                }
                this.activeTooltipSprite = sprite;
            });
            sprite.events.on('globalpointermove', (event) => {
                pointerMoveEvent = event;
            });

            const originalHide = sprite.hideTooltip.bind(sprite);
            // Bypasses the "keep tooltip open" hack below so another bullet's
            // pointerover can always force this tooltip closed.
            sprite.forceHideTooltip = (): Promise<void> | undefined => {
                if (this.activeTooltipSprite === sprite) {
                    this.activeTooltipSprite = null;
                }
                return originalHide();
            };
            sprite.hideTooltip = (): Promise<void> | undefined => {
                if (pointerOverEvent && pointerMoveEvent && pointerMoveEvent.point.y > pointerOverEvent.point.y || !directionCheck) {
                    if (this.activeTooltipSprite === sprite) {
                        this.activeTooltipSprite = null;
                    }
                    return originalHide();
                }
                directionCheck = false;
            };
            if (chartSeries.bulletType === ChartBulletTypesEnum.RIGHT_ARROW) {
                sprite = this.setArrowHeadDirection(this.root, s as LineSeries, chartSeries, chartSeriesIndex);
                sprite.set('tooltipText', this.config.useTooltip ? (chartSeries.tooltipHTML  || ' ') : null);
                sprite.set('tooltip', tooltip);
            }

            series.events.on('datavalidated', () => {
                if (dataItem.get('valueY') === undefined) {
                    return;
                }
                const isVisible = series.dataItems.includes(dataItem);
                sprite.set('forceHidden', !isVisible);
            });

            const bullet = am5.Bullet.new(this.root, {
                sprite: sprite
            });
            this.bullets.push(bullet);

            return bullet;
        });
    }

    setArrowHeadDirection(root: am5.Root, series: LineSeries, chartSeries: ChartSeries, chartSeriesIndex: number): am5.Container {
        const size = 10;

        const container = am5.Container.new(root, {
            centerX: am5.p100,
            centerY: am5.p50,
            interactive: true,
            focusable: true,
            hoverOnFocus: true,
            showTooltipOn: 'hover',
        });

        container.children.push(am5.Graphics.new(root, {
            fill: am5.color('#ffffff'),
            fillOpacity: 0.001,
            strokeOpacity: 0,
            interactive: false,
            draw(display) {
                display.moveTo(0, -size * 0.7);
                display.lineTo(-size * 0.9, -size * 0.7);
                display.lineTo(-size * 0.9, size * 0.7);
                display.lineTo(0, size * 0.5);
                display.closePath();
            }
        }));

        container.children.push(am5.Graphics.new(root, {
            stroke: series.get('stroke'),
            strokeWidth: 2,
            fillOpacity: 0,
            strokeOpacity: 1,
            interactive: false,
            draw(display) {
                display.moveTo(0, 0);
                display.lineTo(-size, -size * 0.7);
                display.moveTo(0, 0);
                display.lineTo(-size, size * 0.7);
            }
        }));

        const xAxis = this.dateAxis;
        const yAxis = this.valueAxes[chartSeries.axisIndex];

        const updateAngle = () => {
            const dataItems = series.dataItems;
            if (dataItems.length < 2)  {
                return container;
            }

            const lastItem = dataItems[dataItems.length - 1];

            // Find the nearest non-null item before the last
            let prevItem;
            for (let i = dataItems.length - 2; i >= 0; i--) {
                if (dataItems[i].dataContext['value' + chartSeriesIndex] != null) {
                    prevItem = dataItems[i];
                    break;
                }
            }
            if (!prevItem) {
                return container;
            }

            const p1x = xAxis.get('renderer').positionToCoordinate(xAxis.valueToPosition(prevItem.dataContext['key']));
            const p1y = yAxis.get('renderer').positionToCoordinate(yAxis.valueToPosition(prevItem.dataContext['value' + chartSeriesIndex]));
            const p2x = xAxis.get('renderer').positionToCoordinate(xAxis.valueToPosition(lastItem.dataContext['key']));
            const p2y = yAxis.get('renderer').positionToCoordinate(yAxis.valueToPosition(lastItem.dataContext['value' + chartSeriesIndex]));


            const angle = Math.atan2(p2y - p1y, p2x - p1x) * 180 / Math.PI;
            container.set('rotation', angle);
        };

        series.events.on('datavalidated', () => {
            root.events.once('frameended', updateAngle);
        });
        return container;
    }

    setUpStackedSeriesTooltip(
        series: SmoothedXLineSeries,
        chartSeries: ChartSeries,
        chartSeriesIndex: number
    ): void {
        const tooltip = am5.Tooltip.new(this.root, {
            getFillFromSprite: false,
            labelHTML: chartSeries.tooltipHTML,
            keepTargetHover: true,
            hoverOnFocus: true
        });
        (tooltip.get('background') as am5.PointedRectangle).setAll({
            fill: am5.color(chartColors.agprWhite),
            fillOpacity: 1,
            stroke: am5.color('#ddd'),
            cornerRadius: 8
        });

        tooltip.label.events.on('click', (ev) => {
            const anchor = ((ev.originalEvent as PointerEvent)?.target as HTMLElement)
                ?.closest?.('a.viewMentionLink') as HTMLAnchorElement | null;
            const index = anchor?.dataset?.seriesIndex;
            const url = index != null
                ? (tooltip.label.dataItem?.dataContext as Record<string, string>)?.['viewUrl' + index]
                : undefined;
            if (url) {
                (ev.originalEvent as MouseEvent)?.preventDefault?.();
                (ev.originalEvent as MouseEvent)?.stopPropagation?.();
                this.tooltipLinkClicked$.next(url);
            }
        });

        series.bullets.push(() => {
            const sprite = am5.Circle.new(this.root, {
                radius: 7,
                fill: am5.color(chartColors.agprWhite),
                fillOpacity: 0,
                interactive: true,
                showTooltipOn: 'hover',
                tooltipText: ' ',
                tooltip: tooltip
            });
            return am5.Bullet.new(this.root, { sprite });
        });
    }

    setUpColumnSeries(
        color: string,
        seriesIndex: number,
        metricType: ChartMetricsEnum,
        chartSeries: ChartSeries
    ): ColumnSeries {
        this.dateAxis.setAll({
            startLocation: 0,
            endLocation: 1
        });
        const columnSeries = ColumnSeries.new(this.root, {
            xAxis: this.dateAxis,
            yAxis: this.valueAxes[chartSeries.axisIndex],
            valueXField: 'key',
            valueYField: 'value' + seriesIndex,
            valueYGrouped: 'sum',
            stroke: am5.color(color),
            fill: am5.color(color),
            tooltip: undefined,
            maskBullets: false,
            groupDataCallback: this.groupDataItem.bind(this)
        });

        const tooltip = am5.Tooltip.new(this.root, {
            getFillFromSprite: false,
            labelHTML: chartSeries.tooltipHTML,
            hoverOnFocus: true,
            keepTargetHover: true,
            autoTextColor: false
        });

        tooltip.label.set('fill', am5.color('#444444'));

        (tooltip.get('background') as am5.PointedRectangle).setAll({
            fill: am5.color(chartColors.agprWhite),
            fillOpacity: 1,
            stroke: am5.color('#ddd'),
            cornerRadius: 8
        });

        columnSeries.columns.template.setAll({
            width: am5.percent(80),
            hoverOnFocus: true,
            focusable: true,
            interactive: true,
            tooltipText: this.config.useTooltip ? (chartSeries.tooltipText  || ' ') : null,
            tooltip: tooltip,
            tooltipHTML: this.config.useTooltip ? chartSeries.tooltipHTML : null
        });

        tooltip.events.on('click', (event) => {
            this.tooltipLinkClicked$.next((event.target as am5.Tooltip).label.dataItem.dataContext?.['viewUrl' + seriesIndex]);
        });

        let srText = '{label}, ';
        if (metricType === ChartMetricsEnum.ave) {
            const currency = this.settings.currencyCode;
            if (currency) {
                const currencySymbol = currencySymbolMap.get(currency);
                if (currencySymbol) {
                    srText += `{value${seriesIndex}.formatNumber('${ currencySymbol }#.#a')}`;
                } else {
                    srText += `{value${seriesIndex}.formatNumber('#.#a ${ currency }')}`;
                }
            }
        } else {
            srText += `{value${seriesIndex}.formatNumber(\'#.#a\')}`;
        }
        columnSeries.set('ariaLabel', srText);

        return this.amchart.series.push(columnSeries);
    }

    groupDataItem(dataItem: am5.DataItem<IXYSeriesDataItem>, interval: am5.time.ITimeInterval): void {
        const originals = dataItem.get('originals');

        const seriesName = (dataItem.component as XYSeries)['name'];
        const seriesIndex = this.series.findIndex(x => x.label === seriesName);
        if (seriesIndex === -1) {
            return;
        }

        let value = 0, mentionCount = 0, missingValue = false;
        originals.forEach(x => {
            const currentValue = x.dataContext['value' + seriesIndex];
            if (!currentValue && currentValue !== 0) {
                missingValue = true;
            }
            value += x.dataContext['value' + seriesIndex];
            mentionCount += x.dataContext['mentionCount'];
        });
        if (!missingValue) {
            dataItem.dataContext['value' + seriesIndex] = value;
        }
        dataItem.dataContext['mentionCount'] = mentionCount;
        // todo: set percent value
        if (originals.length > 1) {
            const first = originals[0].dataContext as DataPointModel;
            const last = originals[originals.length - 1].dataContext as DataPointModel;
            const startMoment = moment(first.rawStartDate);
            const endMoment = moment(last.rawEndDate);
            dataItem.dataContext['rawStartDate'] = first['rawStartDate'];
            dataItem.dataContext['rawEndDate'] = last['rawEndDate'];
            dataItem.dataContext['startTimeExport'] = first['startTimeExport'];
            dataItem.dataContext['endTimeExport'] = last['endTimeExport'];
            const dateRange = new DateQueryModel();
            dateRange.type = DateQueryTypeEnum.ABSOLUTE;
            dateRange.absoluteRange = {
                start: startMoment.toDate(),
                end: endMoment.toDate()
            };
            const originalUrl = originals
                .map(o => o.dataContext['viewUrl' + seriesIndex] as string)
                .find(url => !!url);
            if (originalUrl?.includes('filters=')) {
                const searchCriteriaModel = MentionSearchCriteriaModel.fromFilterQueryString(originalUrl.split('filters=')[1]);
                searchCriteriaModel.search.query.dateQuery = DateQueryModel.makeAbsoluteRange(dateRange);
                dataItem.dataContext['viewUrl' + seriesIndex] = originalUrl.split('filters=')[0] + 'filters='
                    + MentionSearchCriteriaModel.toFilterQueryString(searchCriteriaModel.search, searchCriteriaModel.seriesUniqueUuid);
            } else {
                const filters = new MentionSearchCriteriaModel();
                filters.query.dateQuery = DateQueryModel.makeAbsoluteRange(dateRange);
                // some series (eg external traffic in coverage impact trend) have no mention drilldown
                if (this.series[seriesIndex].getSeriesUrl) {
                    dataItem.dataContext['viewUrl' + seriesIndex] = this.series[seriesIndex].getSeriesUrl(filters);
                }
            }

            const label = `${moment(first.rawStartDate).format('ddd MMM D YYYY')} - ${moment(last.rawEndDate).format('ddd MMM D YYYY')}`;
            dataItem.dataContext['label'] = label;
        }
    }

    setUpLegend(): void {
        if (this.config.customLegend?.legendContainerElement || !this.config.hasLegend) {
            return;
        }
        if (this.config.isSparkline) {
            return;
        }
        const legendSettings: am5.ILegendSettings = {
            x: am5.p50,
            centerX: am5.p50,
            paddingTop: 10,
            width: am5.p100,
        };
        if (!this.config.disableLegendScroll) {
            legendSettings.verticalScrollbar = am5.Scrollbar.new(this.root, { orientation: 'vertical' });
            if (this.config.padding?.noPadding) {
                this.amchart.set('paddingBottom', 10);
            }
        }
        if (this.amchart.series.length > 1) {
            legendSettings.x = am5.p0;
            legendSettings.centerX = am5.p0;
        }
        if (this.config.centerLegend) {
            legendSettings.x = am5.p50;
            legendSettings.centerX = am5.p50;
        }
        if (this.config.disableLegendClick) {
            legendSettings.clickTarget = 'none';
        }
        const legend = this.amchart.children.push(am5.Legend.new(this.root, legendSettings));
        legend.labels.template.setAll({
            fontFamily: 'Poppins, Arial, sans-serif',
            fontSize: this.config.legendFontSize || '0.875rem',
        });
        if (this.config.legendItemWidth) {
            legend.itemContainers.template.set('width', this.config.legendItemWidth);
            legend.labels.template.set('oversizedBehavior', 'truncate');
        }
        if (this.config.legendMaxColumns) {
            legend.set('layout', am5.GridLayout.new(this.root, {
                maxColumns: this.config.legendMaxColumns,
                fixedWidthGrid: true
            }));
        }
        legend.markers.template.set('scale', 0.8);
        legend.itemContainers.template.setAll({
            maxWidth: this.config.legendItemWidth || 130,
            scale: 1,
            paddingTop: 1,
            paddingBottom: 1,
            paddingLeft: 10
        });
        if (this.config.legendItemWidth) {
            legend.labels.template.set('maxWidth', this.getLegendLabelMaxWidth(legend, this.config.legendItemWidth));
        }

        const legendData = this.config.isStacked
            ? [...this.amchart.series.values].reverse()
            : this.amchart.series.values;

        legend.data.setAll(legendData);
        this.legend = legend;

        if (this.config.legendItemWidth && this.config.legendMaxColumns) {
            this.amchart.events.on('boundschanged', () => this.updateLegendItemWidth());
        }

        if (this.config.isStacked) {
            // For stacked area series amchart 5 draws the series' stroke line across each legend
            // marker, hide it so the markers read as solid colour squares instead of a filled box
            // with a line through the middle
            this.root.events.once('frameended', () => this.hideLegendMarkerStrokes(legend));
        }
    }

    private updateLegendItemWidth(): void {
        const width = this.amchart?.innerWidth();
        if (!this.legend || !Number.isFinite(width) || width <= 0) {
            return;
        }
        const columns = this.config.legendMaxColumns;
        const itemWidth = Math.min(this.config.legendItemWidth, width / columns);
        this.legend.itemContainers.template.setAll({
            width: itemWidth,
            maxWidth: itemWidth
        });
        this.legend.labels.template.set('maxWidth', this.getLegendLabelMaxWidth(this.legend, itemWidth));
    }

    private getLegendLabelMaxWidth(legend: am5.Legend, itemWidth: number): number {
        const itemContainer = legend.itemContainers.template;
        const paddings = itemContainer.get('paddingLeft', 0) + itemContainer.get('paddingRight', 0)
            + (legend.markers.template.get('width', 18) as number) + legend.labels.template.get('marginLeft', 0);
        return Math.max(itemWidth - paddings, 0);
    }

    private hideLegendMarkerStrokes(legend: am5.Legend): void {
        if (!legend || legend.isDisposed()) {
            return;
        }
        legend.dataItems.forEach(dataItem => {
            const marker = dataItem.get('marker');
            marker?.children.each(child => {
                if ((child.get('themeTags') as string[] | undefined)?.includes('stroke')) {
                    child.set('forceHidden', true);
                }
            });
        });
    }

    setUpScrollbar(): void {
        if (this.amchart && this.isSingleSeries && this.config.useScrollbar) {
            const scrollbar = am5.Scrollbar.new(this.root, {
                orientation: 'horizontal',
                dx: -15,
                maxHeight: 10
            });
            scrollbar.startGrip.set('scale', 0.8);
            scrollbar.endGrip.set('scale', 0.8);
            this.amchart.set('scrollbarX', scrollbar);
            this.amchart.topAxesContainer.set('paddingTop', -10);
            this.amchart.topAxesContainer.set('paddingBottom', 10);
        }
    }

    /**
     * date line charts can optionally have a set of 'events', which are just things
     * with an associated date, name, and color. they produce vertical lines in the chart,
     * which in amcharts are called 'axisRanges', and used to be called 'guides'.
     */
    handleDateAxisRange(): void {
        const dateAxisRanges = this.settings.events || [];
        for (let i = 0; i < dateAxisRanges.length; i++) {

            const dateAxisRange = dateAxisRanges[i];
            const color = dateAxisRange.color;

            let tooltipHTML = `<center style="font-size: 0.875rem; padding: 3px 0 5px 0;">${dateAxisRange.name}</center>`;
            if (dateAxisRange.date) {
                const date = new Date(dateAxisRange.date.absoluteDate);
                tooltipHTML += `<center style="font-size: 0.875rem; padding-bottom: 5px;">${date.toDateString()}</center>`;
            }
            if (dateAxisRange.typeLabel) {
                tooltipHTML += `<center style="font-size: 0.875rem; padding-bottom: 3px;">${dateAxisRange.typeLabel}</center>`;
            }

            if (dateAxisRange.tooltipHTML) {
                tooltipHTML = dateAxisRange.tooltipHTML;
            }

            const dataItem = this.dateAxis.makeDataItem({
                value: dateAxisRange.date ? new Date(dateAxisRange.date.absoluteDate).getTime() : 0
            });
            const axisRange = this.dateAxis.createAxisRange(dataItem);

            axisRange.get('grid').setAll({
                stroke: am5.color(color),
                strokeWidth: 3.5,
                strokeOpacity: 1,
                visible: true,
                // A sprite only shows its tooltip on hover when it has content of its own to put in it
                interactive: true,
                tooltipHTML: tooltipHTML,
                tooltip: this.dateAxisRangeTooltip(tooltipHTML, !!dateAxisRange.tooltipHTML)
            });

            const circle = am5.Circle.new(this.root, {
                radius: 3.5,
                interactive: true,
                tooltipHTML: tooltipHTML,
                tooltip: this.dateAxisRangeTooltip(tooltipHTML, !!dateAxisRange.tooltipHTML),
                ...(!this.config.ignoreBullets
                    ? {
                        fill: am5.color(chartColors.agprWhite),
                        stroke: am5.color(color),
                        strokeWidth: 1
                    }
                    : {
                        fill: am5.color(color),
                        strokeWidth: 5,
                        strokeOpacity: 0
                    }
                )
            });

            dataItem.set('bullet', AxisBullet.new(this.root, {
                sprite: circle
            }));
        }
    }

    /** An event's own tooltip goes on the same white card the series bullets use */
    private dateAxisRangeTooltip(labelHTML: string, isCustom: boolean): am5.Tooltip {
        const tooltip = am5.Tooltip.new(this.root, { labelHTML: labelHTML, getFillFromSprite: !isCustom });

        if (isCustom) {
            (tooltip.get('background') as am5.PointedRectangle).setAll({
                fill: am5.color(chartColors.agprWhite),
                fillOpacity: 1,
                stroke: am5.color('#ddd'),
                cornerRadius: 8
            });
        }

        return tooltip;
    }

    public updateData(data: DataPointModel[]): void {
        this.rawData = data;
        if (!this.amchart) {
            return;
        }
        if (this.isContentEmpty()) {
            data = null;
            this.showNoContentMessage();
            this.dateAxis?.set('visible', false);
            this.valueAxes.forEach(axis => {
                if (axis) {
                    axis.set('visible', false);
                    axis.get('renderer')?.grid?.template.set('visible', false);
                }
            });
            this.dateAxis?.axisRanges?.each(range => range.hide());
            this.amchart.get('scrollbarX')?.set('visible', false);
            this.legend?.set('visible', false);
            // Clear stale series data so the chart canvas doesn't keep showing a previous
            // successful render underneath the "no content" message.
            this.amchart.series.each(s => s.data.setAll([]));
        } else {
            this.hideNoContentMessage();
            this.dateAxis?.set('visible', true);
            this.valueAxes.forEach(axis => {
                if (axis) {
                    axis.set('visible', true);
                    axis.get('renderer')?.grid?.template.set('visible', true);
                }
            });
            this.legend?.set('visible', true);
        }
        if (data) {
            this.updateDateAxisBaseInterval(data);
            this.amchart.series.each((s, i) => {
                if (this.series[i].indicatorTargetIndex !== undefined) {
                    return;
                }
                s.data.setAll(this.prepareSeriesData(data, s, i));
            });
        }

        if (this.config?.forceInverseMinMax) {
            const ABS_MIN_VALUE = 1;
            const ABS_MAX_VALUE = 2;
            const maxValue = Math.max(ABS_MAX_VALUE, Math.ceil(maxBy(this.rawData, 'value')?.value || ABS_MAX_VALUE));
            const minValue = Math.max(ABS_MIN_VALUE, Math.floor(minBy(this.rawData, 'value')?.value || ABS_MIN_VALUE));

            // set the min & max chart limits
            // if lowest is 2.4, we want the legend to show 2
            // if the highest is 5.6, we want to show 6 in the legend
            this.valueAxes?.[0]?.set('max', maxValue);
            this.valueAxes?.[0]?.set('min', minValue);

            this.valueAxes?.[0]?.get('renderer').labels.template.adapters.add('text', (text, target) => {
                const dataItem = target.dataItem as am5.DataItem<am5xy.IValueAxisDataItem> | undefined;
                if (dataItem) {
                    const val = dataItem.get('value');
                    if (val === minValue) {
                        return minValue + '';
                    }
                    if (val === maxValue) {
                        return maxValue + '';
                    }
                    // force to a max of 2 decimal points
                    return val !== undefined ? (Math.round(val * 100) / 100).toString() : text;
                }

                return text;
            });

            // Force add min horizontal bar (sometimes it won't show lowest value, depending onthe chart)
            const minDataItem = this.valueAxes?.[0]?.makeDataItem({value: minValue});
            const minRange = this.valueAxes?.[0]?.createAxisRange(minDataItem);
            minRange?.get('grid')?.setAll({forceHidden: false, strokeOpacity: 0.8, stroke: am5.color(0xced4da)});

            if (maxValue > ABS_MAX_VALUE) {
                // Force add max horizontal bar (sometimes it won't show lowest value, depending onthe chart)
                const maxDataItem = this.valueAxes?.[0]?.makeDataItem({value: maxValue});
                const maxRange = this.valueAxes?.[0]?.createAxisRange(maxDataItem);
                maxRange?.get('grid')?.setAll({forceHidden: false, strokeOpacity: 0.8, stroke: am5.color(0xced4da)});
            }
        }

        this.dataUpdated$.next(true);
    }

    private updateDateAxisBaseInterval(data: DataPointModel[]): void {
        if (!this.dateAxis || !data.length) {
            return;
        }
        const from = data[0].key as number;
        const to = data[data.length - 1].key as number;
        const totalHours = moment(to).diff(moment(from), 'hours');
        const baseInterval = this.timeHelper.getDateGranularity(
            totalHours, this.settings.timeInterval
        ) as { timeUnit: TimeUnit; count: number };
        this.dateAxis.set('baseInterval', baseInterval);
    }

    public updateIndicatorData(data: DataPointModel[]): void {
        if (!this.amchart || !data) {
            return;
        }
        this.amchart.series.each((s, i) => {
            if (this.series[i].indicatorTargetIndex === undefined) {
                return;
            }
            s.data.setAll(this.prepareSeriesData(data, s, i));
        });
    }

    private prepareSeriesData(data: DataPointModel[], s: XYSeries, i: number): DataPointModel[] {
        const d = structuredClone(data);
        if (!d.length) {
            return d;
        }
        if (d.length <= this.maxCountForGrouping) {
            s.set('groupDataDisabled', true);
        }
        d.forEach(x => {
            x.color = this.settings.colorMap?.[this.series[i]['colorMapKey'] ?? ''];
        });

        return d;
    }

    public setUpExporting(): void {
        const scrollbar = this.amchart.get('scrollbarX');
        const zoomOutButton = this.amchart.zoomOutButton;
        let exportedLegendMaxHeight: number;
        this.exporting.events.on('exportstarted', () => {
            scrollbar?.hide(0);
            zoomOutButton.set('forceHidden', true);
            exportedLegendMaxHeight = this.legend?.get('maxHeight') as number;
            this.legend?.set('maxHeight', undefined);
        });

        this.exporting.events.on('exportfinished', () => {
            if (this.isSingleSeries) {
                scrollbar?.show(0);
            }
            zoomOutButton.set('forceHidden', true);
            this.legend?.set('maxHeight', exportedLegendMaxHeight);

        });
        // todo: check long name sources
    }

    protected override restoreExportState(): void {
        const scrollbar = this.amchart.get('scrollbarX');
        const zoomOutButton = this.amchart.zoomOutButton;
        if (this.isSingleSeries) {
            scrollbar?.show(0);
        }
        zoomOutButton.set('forceHidden', true);
    }

    async export(type: string, fileName: string): Promise<void> {
        this.exportData = this.isDynamicInterval
            ? this.ungroupedExportData ?? this.rawData
            : this.amchart.series.values[0].dataItems.map(x => x.dataContext) as DataPointModel[];
        await super.export(type, fileName);
    }

    public isContentEmpty(): boolean {
        return this.rawData.length === 0;
    }

    public clone(): Am5DateValueXYChart {
        const clone = new Am5DateValueXYChart(
            this.series.map(s => JSON.parse(JSON.stringify(s))),
            this.config,
            this.settings
        );
        clone.series.forEach((s, index) => {
            // some series (eg external traffic in coverage impact trend) have no mention drilldown
            if (this.series[index].getSeriesUrl) {
                s.getSeriesUrl = this.series[index].getSeriesUrl.bind(s);
            }
            if (this.series[index].calculateIndicators) {
                s.calculateIndicators = this.series[index].calculateIndicators.bind(s);
            }
        });

        return clone;
    }

    private resetZoomOut() {
        setTimeout(() => {
            this.amchart?.zoomOut();
        }, 500);
    }

    destroy(): void {
        super.destroy();
        this.dateAxis = null;
        this.valueAxis = null;
        this.valueAxes = [];
        this.bullets = [];
        this.legend = null;
    }

    private get isDynamicInterval(): boolean {
        return !this.settings.timeInterval || this.settings.timeInterval === ChartTimeIntervalsEnum.DYNAMIC;
    }
}
